import type { DeviceContext } from "../lib/device";

// PWA install/offline readiness model (GW-PWA-READINESS). Four concise, operational answers to
// "is this phone set up to guard with?" - running mode, offline capture, ONE capture-path line,
// and notifications. Pure derivation over injected facts so every honesty rule is unit-testable.
//
// NFC doctrine (hard rule): a browser without Web NFC - every iOS browser today - must NEVER
// imply NFC could work here; the path line names QR/manual and states NFC's absence plainly.
// Notifications stay "not yet available" until client subscription UI is intentionally wired
// behind the server-owned web_notifications flag - no Notification API calls anywhere here.

export interface PwaCaptureFlags {
  qrCapture: boolean;
  nfcCapture: boolean;
  manualCapture: boolean;
}

export interface PwaReadinessFacts {
  device: DeviceContext;
  /** A service worker currently controls this page (the app shell loads offline). */
  serviceWorkerActive: boolean;
  /** The same local queue storage path used by capture can persist and read back a tiny probe. */
  storageAvailable: boolean;
  captureFlags: PwaCaptureFlags;
  /** Server-resolved web_notifications flag; UI stays honest until registration is intentionally wired. */
  notificationsEnabled: boolean;
}

export interface ReadinessItem {
  key: "mode" | "offline" | "capture-path" | "notifications";
  label: string;
  value: string;
  tone: "ok" | "warn" | "muted";
}

/** True when a service worker controls the current page. Injectable for tests. */
export function detectServiceWorkerActive(nav: Navigator | undefined = globalThis.navigator): boolean {
  try {
    return Boolean(nav && "serviceWorker" in nav && nav.serviceWorker.controller);
  } catch {
    return false;
  }
}

interface StringStorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem?(key: string): void;
}

const STORAGE_PROBE_KEY = "secops.guard.pwaReadinessProbe";

/** True when the same storage class used by the web scan queue can round-trip a tiny value. */
export function detectQueueStorageAvailable(storage: StringStorageLike | undefined): boolean {
  try {
    if (!storage) {
      return false;
    }
    storage.setItem(STORAGE_PROBE_KEY, "1");
    const ok = storage.getItem(STORAGE_PROBE_KEY) === "1";
    storage.removeItem?.(STORAGE_PROBE_KEY);
    return ok;
  } catch {
    return false;
  }
}

function captureMode(facts: PwaReadinessFacts): ReadinessItem {
  if (facts.device.isStandalonePwa) {
    return { key: "mode", label: "Running as", value: "Installed app", tone: "ok" };
  }
  return {
    key: "mode",
    label: "Running as",
    value: "Browser tab - Add to Home Screen to run SecOps Guard as an app",
    tone: "muted",
  };
}

function offlineCapture(facts: PwaReadinessFacts): ReadinessItem {
  if (facts.serviceWorkerActive && facts.storageAvailable) {
    return {
      key: "offline",
      label: "Offline capture",
      value: "Ready - captures queue offline and sync when you are back online",
      tone: "ok",
    };
  }
  if (facts.storageAvailable) {
    return {
      key: "offline",
      label: "Offline capture",
      value: "Partial - captures can queue, but the app needs network to load",
      tone: "warn",
    };
  }
  return {
    key: "offline",
    label: "Offline capture",
    value: "Not available in this browser",
    tone: "muted",
  };
}

function capturePath(facts: PwaReadinessFacts): ReadinessItem {
  const { device, captureFlags } = facts;
  const nfcUsable = device.hasWebNfc && captureFlags.nfcCapture;
  const qrUsable = device.hasCamera && captureFlags.qrCapture;
  const manualUsable = captureFlags.manualCapture;
  if (nfcUsable) {
    if (qrUsable && manualUsable) {
      return {
        key: "capture-path",
        label: "Capture path",
        value: "NFC tap first - QR and manual as backup",
        tone: "ok",
      };
    }
    if (qrUsable) {
      return {
        key: "capture-path",
        label: "Capture path",
        value: "NFC tap first - QR as backup",
        tone: "ok",
      };
    }
    return {
      key: "capture-path",
      label: "Capture path",
      value: manualUsable ? "NFC tap first - manual as backup" : "NFC tap first",
      tone: "ok",
    };
  }
  if (qrUsable) {
    return {
      key: "capture-path",
      label: "Capture path",
      value: manualUsable
        ? "QR first - manual as backup (NFC not available in this browser)"
        : "QR first (NFC not available in this browser)",
      tone: "ok",
    };
  }
  if (!manualUsable) {
    return {
      key: "capture-path",
      label: "Capture path",
      value: "No capture method available on this device",
      tone: "warn",
    };
  }
  return {
    key: "capture-path",
    label: "Capture path",
    value: "Manual entry only on this device",
    tone: "muted",
  };
}

function notifications(facts: PwaReadinessFacts): ReadinessItem {
  if (facts.notificationsEnabled) {
    return {
      key: "notifications",
      label: "Notifications",
      value: "Enabled for this account - web registration is coming soon",
      tone: "warn",
    };
  }
  return {
    key: "notifications",
    label: "Notifications",
    value: "Not enabled on web - the mobile app carries alerts today",
    tone: "muted",
  };
}

export function derivePwaReadiness(facts: PwaReadinessFacts): ReadinessItem[] {
  return [captureMode(facts), offlineCapture(facts), capturePath(facts), notifications(facts)];
}
